import { useState, useEffect, useMemo, useRef } from "react";
import { Box, Typography, Paper, Button, useTheme, IconButton, Tooltip, Stack, Skeleton } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import AddIcon from "@mui/icons-material/Add";
import ReplayIcon from "@mui/icons-material/Replay";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import extractYouTubeId from "../utils/extractYouTubeId";
import Prism from "prismjs";
import "prismjs/themes/prism.css";
import "prismjs/themes/prism-tomorrow.css";
import parse, { domToReact } from "html-react-parser";
import DOMPurify from "dompurify";
import DynamicCodeEditor from "../codeEditor/DynamicCodeEditor";
import EditableCodeSection from "./EditableCodeSection";
import { updateContent } from "../../features/content/contentSlice";
import { Link as RouterLink, useLocation } from "react-router-dom";
import LockPersonIcon from '@mui/icons-material/LockPerson';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import QuizView from "./QuizView";
import LessonHeader from "./LessonHeader";
import PredictOutput from "../codeEditor/PredictOutput";

// 🔑 REQUIRED for inline SVG rendering
DOMPurify.addHook("uponSanitizeElement", (node, data) => {
    if (data.tagName === "svg") {
        node.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    }
});

export function cleanHTMLContent(html) {
    if (!html) return "";

    let cleaned = html
        .replace(/<html[^>]*>/gi, "")
        .replace(/<\/html>/gi, "")
        .replace(/<body[^>]*>/gi, "")
        .replace(/<\/body>/gi, "");

    return DOMPurify.sanitize(cleaned, {
        KEEP_CONTENT: true,           // ✅ critical for SVG children
        ALLOW_DATA_ATTR: true,        // ✅ allow custom data-* attributes for PredictOutput

        ADD_TAGS: [
            "svg", "g", "line", "circle", "rect", "text", "path", "ellipse", "tspan",
            "polyline", "polygon", "defs",
            "linearGradient", "radialGradient", "stop", "clipPath",
            "mask", "marker", "image", "pattern", "symbol", "use",
            "animate", "animateMotion", "animateTransform", "mpath", "set",
            "table", "thead", "tbody", "tr", "th", "td", "details", "summary"
        ],

        ADD_ATTR: [
            "xmlns", "viewBox", "width", "height", "preserveAspectRatio",
            "x", "y", "x1", "y1", "x2", "y2", "cx", "cy", "r", "rx", "ry", "d", "points",
            "transform", "opacity", "fill", "fill-opacity", "stroke", "stroke-width",
            "stroke-dasharray", "stroke-dashoffset", "stroke-linecap", "stroke-linejoin",
            "font-size", "font-weight", "font-family", "text-anchor", "dominant-baseline",
            "attributeName", "attributeType", "begin", "dur", "from", "to", "by", "values",
            "keyTimes", "keySplines", "calcMode", "repeatCount", "repeatDur", "accumulate", "additive", "rotate",
            "type", "gradientUnits", "gradientTransform", "spreadMethod", "offset", "stop-color", "stop-opacity",
            "clip-path", "mask", "marker-start", "marker-mid", "marker-end", "id", "class", "style", "href", "xlink:href",
            "open", "colspan", "rowspan", "data-code", "data-options", "data-correct", "data-explanation", "data-index"
        ],
    }).trim();
}

// 💡 Custom CollapsibleDetails Component to ensure cross-browser/framework reliability
function CollapsibleDetails({ summaryText, children }) {
    const [isOpen, setIsOpen] = useState(false);
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    return (
        <Box
            sx={{
                border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
                borderRadius: "10px",
                margin: "16px 0",
                backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)",
                overflow: "hidden",
                transition: "all 0.2s ease-in-out",
            }}
        >
            {/* Summary Header */}
            <Box
                onClick={() => setIsOpen(!isOpen)}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.2,
                    padding: "14px 18px",
                    cursor: "pointer",
                    userSelect: "none",
                    fontWeight: 700,
                    fontSize: "0.92rem",
                    color: isDark ? "#ffa4a2" : "#e50914",
                    "&:hover": {
                        backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.02)",
                    },
                }}
            >
                <span style={{
                    transform: isOpen ? "rotate(90deg)" : "rotate(0deg)",
                    transition: "transform 0.2s ease",
                    display: "inline-block",
                    lineHeight: 1,
                    fontSize: "0.75em"
                }}>
                    ▶
                </span>
                {summaryText}
            </Box>

            {/* Collapsible Content */}
            {isOpen && (
                <Box
                    sx={{
                        padding: "18px",
                        borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)"}`,
                        backgroundColor: isDark ? "rgba(0,0,0,0.12)" : "#ffffff",
                    }}
                >
                    {children}
                </Box>
            )}
        </Box>
    );
}

export default function MiddleContent({ selectedContent, scrolledDown, onContentUpdate, loading, error, onRetry, focusMode, onToggleFocusMode }) {
    const [showVideo, setShowVideo] = useState(true);
    const [codeSections, setCodeSections] = useState([]);
    const [newlyAddedIndex, setNewlyAddedIndex] = useState(null); // Track newly added section
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.auth);
    const isAdmin = user?.role === "admin";
    const location = useLocation();
    const colors = {
        primary: "#e50914",
        secondary: "#6a0dad",
    };

    // Extract code sections from HTML
    // Order matters: wait for both content_text AND content ID to be available before extracting
    // This ensures we extract from the correct content when switching subjects/contentIds
    const prevContentIdRef = useRef(null);
    useEffect(() => {
        const currentContentId = selectedContent?.id;
        const hasContentText = !!selectedContent?.content_text;

        // If no content ID, clear sections and return
        if (!currentContentId) {
            if (codeSections.length > 0) {
                setCodeSections([]);
            }
            prevContentIdRef.current = null;
            return;
        }

        // Check if this is a new content (different ID)
        const isNewContent = prevContentIdRef.current !== currentContentId;

        // If content ID changed, clear previous sections
        if (isNewContent) {
            setCodeSections([]);
        }

        // Only extract if we have content_text
        // If this is new content but no content_text yet, wait for it to load
        if (!hasContentText) {
            return;
        }

        // If we already have sections for this content ID, don't re-extract
        // (unless it's new content, which we already cleared above)
        if (!isNewContent && codeSections.length > 0) {
            return;
        }

        // We're about to extract - update ref to track this content
        prevContentIdRef.current = currentContentId;

        try {
            const safeHTML = cleanHTMLContent(selectedContent.content_text);
            const parser = new DOMParser();
            const doc = parser.parseFromString(safeHTML, "text/html");
            const preElements = doc.querySelectorAll("pre");

            // Filter to extract runnable code blocks, excluding only explicit visual block diagrams and output boxes
            const diagramSymbols = ["┌", "├", "└", "──►", "│  ", "├──", "└──", "┌──", "═", "▼", "▲"];
            const codePreElements = Array.from(preElements).filter((pre) => {
                const codeElement = pre.querySelector("code");
                const hasLanguageClass = (pre.className && pre.className.includes("language-")) ||
                                         (codeElement && codeElement.className && codeElement.className.includes("language-"));
                
                if (codeElement || hasLanguageClass) {
                    return true;
                }

                const htmlContent = pre.outerHTML || pre.innerHTML || "";
                const textContent = pre.textContent || pre.innerText || "";

                const hasDiagramSymbol = diagramSymbols.some(sym => textContent.includes(sym));
                const hasDiagramBorder = htmlContent.includes("border-left:5px solid #e65100") ||
                                         htmlContent.includes("border-left: 5px solid #e65100");
                const hasOutputBorder = htmlContent.includes("border-left:4px solid #1565c0") ||
                                        htmlContent.includes("border-left: 4px solid #1565c0");

                // If it explicitly matches diagram symbols or diagram/output borders without a <code> tag, skip it
                if (hasDiagramSymbol || hasDiagramBorder || hasOutputBorder) {
                    return false;
                }

                // Treat all other pre blocks (e.g. JavaScript, HTML, CSS, SQL snippets) as code blocks
                return true;
            });

            const sections = codePreElements.map((pre, index) => {
                let code = "";
                const codeElement = pre.querySelector("code");

                if (codeElement) {
                    code = codeElement.textContent || codeElement.innerText || "";
                } else {
                    code = pre.textContent || pre.innerText || "";
                }

                let lang = selectedContent.subject?.toLowerCase() || "text";
                if (codeElement && codeElement.className) {
                    const langClass = codeElement.className.match(/language-(\w+)/);
                    if (langClass) {
                        lang = langClass[1];
                    }
                } else if (pre.className) {
                    const langClass = pre.className.match(/language-(\w+)/);
                    if (langClass) {
                        lang = langClass[1];
                    }
                }

                return {
                    index,
                    code: code.trim(),
                    language: lang,
                    originalHTML: pre.outerHTML,
                };
            });

            // console.log(`Extracted ${sections.length} code sections for content ID ${currentContentId}, subject: ${selectedContent.subject}`);
            setCodeSections(sections);
        } catch (err) {
            console.error("Error extracting code sections:", err);
            setCodeSections([]);
        }
    }, [selectedContent?.id, selectedContent?.content_text, selectedContent?.subject, codeSections.length]);

    useEffect(() => {
        const t = setTimeout(() => Prism.highlightAll(), 0);
        return () => clearTimeout(t);
    }, [selectedContent, codeSections]);

    // Function to update HTML with modified code sections
    const updateHTMLWithCodeSections = (html, sections) => {
        let updatedHTML = html;
        sections.forEach((section, idx) => {
            // Find the original pre tag and replace it
            if (section.originalHTML) {
                // Escape special regex characters in originalHTML
                const escapedOriginal = section.originalHTML.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                const newPreHTML = `<pre><code class="language-${section.language}">${section.code.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</code></pre>`;
                // Replace only the first occurrence to avoid replacing multiple similar sections
                updatedHTML = updatedHTML.replace(escapedOriginal, newPreHTML);
            } else {
                // If no originalHTML, append at the end
                const newPreHTML = `<pre><code class="language-${section.language}">${section.code.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</code></pre>`;
                updatedHTML = updatedHTML + "\n" + newPreHTML;
            }
        });
        return updatedHTML;
    };

    // Handle code section edit
    const handleCodeSectionSave = async (index, newCode, newLanguage) => {
        // console.log("handleCodeSectionSave called:", {
        //     index,
        //     newCode,
        //     newLanguage,
        //     newCodeLength: newCode?.length,
        //     codeSectionsLength: codeSections.length,
        //     currentSectionCode: codeSections[index]?.code,
        //     currentSectionLanguage: codeSections[index]?.language
        // });

        // Validate inputs
        if (!newCode && newCode !== "") {
            console.error("Invalid newCode:", newCode);
            alert("Error: Code content is invalid. Please try again.");
            return;
        }

        if (!newLanguage) {
            console.error("Invalid newLanguage:", newLanguage);
            alert("Error: Language is invalid. Please try again.");
            return;
        }

        const updatedSections = [...codeSections];
        if (index >= 0 && index < updatedSections.length) {
            updatedSections[index] = {
                ...updatedSections[index],
                code: newCode,
                language: newLanguage,
            };
        } else {
            console.error("Invalid index for code section save:", index, "sections length:", updatedSections.length);
            alert("Error: Invalid code section index. Please refresh and try again.");
            return;
        }
        setCodeSections(updatedSections);

        // Update the HTML content
        const updatedHTML = updateHTMLWithCodeSections(
            cleanHTMLContent(selectedContent.content_text),
            updatedSections
        );

        // console.log("Updated HTML preview:", {
        //     originalLength: selectedContent.content_text?.length,
        //     updatedLength: updatedHTML.length,
        //     hasNewCode: updatedHTML.includes(newCode.substring(0, 50))
        // });

        // Save to backend
        try {
            const payload = {
                id: selectedContent.id,
                data: {
                    ...selectedContent,
                    content_text: updatedHTML,
                },
            };
            // console.log("Sending payload to backend:", {
            //     id: payload.id,
            //     content_text_length: payload.data.content_text?.length,
            //     hasNewCode: payload.data.content_text?.includes(newCode.substring(0, 50))
            // });

            await dispatch(updateContent(payload)).unwrap();

            // Update the originalHTML in the section to match the new HTML
            updatedSections[index].originalHTML = `<pre><code class="language-${newLanguage}">${newCode.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</code></pre>`;
            setCodeSections(updatedSections);

            // Don't immediately refresh - the local state update handles the UI
            // Only refresh after a delay to sync with backend if needed
            setTimeout(() => {
                if (onContentUpdate) {
                    onContentUpdate();
                }
            }, 1000);
        } catch (error) {
            console.error("Failed to update content:", error);
            alert("Failed to save changes. Please try again.");
        } finally {

        }
    };

    // Handle code section delete
    const handleCodeSectionDelete = async (index) => {
        const updatedSections = codeSections.filter((_, i) => i !== index);
        setCodeSections(updatedSections);

        // Remove the code section from HTML
        let updatedHTML = cleanHTMLContent(selectedContent.content_text);
        const sectionToRemove = codeSections[index];
        if (sectionToRemove) {
            const originalPattern = sectionToRemove.originalHTML.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            updatedHTML = updatedHTML.replace(originalPattern, '');
        }

        // Save to backend

        try {
            await dispatch(updateContent({
                id: selectedContent.id,
                data: {
                    ...selectedContent,
                    content_text: updatedHTML,
                },
            })).unwrap();

            if (onContentUpdate) {
                onContentUpdate();
            }
        } catch (error) {
            console.error("Failed to delete code section:", error);
            alert("Failed to delete code section. Please try again.");
        } finally {

        }
    };

    // Handle add new code section (at the end)
    const handleAddCodeSection = async () => {
        await handleAddCodeSectionAfter(codeSections.length - 1);
    };

    // Handle add new code section after a specific index
    const handleAddCodeSectionAfter = async (afterIndex) => {
        const newCode = "// Write your code here";
        const newLanguage = selectedContent.subject?.toLowerCase() || "javascript";
        const newPreHTML = `<pre><code class="language-${newLanguage}">${newCode.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</code></pre>`;

        // Get the HTML content
        const currentHTML = cleanHTMLContent(selectedContent.content_text);
        let updatedHTML = "";

        // If adding after a specific section, insert after that section's HTML
        if (afterIndex >= 0 && afterIndex < codeSections.length) {
            const sectionToInsertAfter = codeSections[afterIndex];
            const insertPosition = currentHTML.indexOf(sectionToInsertAfter.originalHTML);
            if (insertPosition !== -1) {
                const insertEnd = insertPosition + sectionToInsertAfter.originalHTML.length;
                updatedHTML =
                    currentHTML.substring(0, insertEnd) +
                    "\n" + newPreHTML +
                    currentHTML.substring(insertEnd);
            } else {
                // Fallback: append at the end
                updatedHTML = currentHTML + "\n" + newPreHTML;
            }
        } else {
            // Append at the end
            updatedHTML = currentHTML + "\n" + newPreHTML;
        }

        // Calculate the index of the newly added section
        const newIndex = afterIndex + 1;
        setNewlyAddedIndex(newIndex); // Mark this section as newly added

        // Save to backend

        try {
            await dispatch(updateContent({
                id: selectedContent.id,
                data: {
                    ...selectedContent,
                    content_text: updatedHTML,
                },
            })).unwrap();

            // Update local state immediately for instant UI update
            const newSection = {
                index: newIndex,
                code: newCode,
                language: newLanguage,
                originalHTML: newPreHTML,
            };
            const updatedSections = [...codeSections];
            updatedSections.splice(newIndex, 0, newSection);
            // Update indices
            updatedSections.forEach((sec, idx) => {
                sec.index = idx;
            });
            setCodeSections(updatedSections);

            // Scroll to the newly added section after a brief delay
            setTimeout(() => {
                const sectionElement = document.querySelector(`[data-code-section-index="${newIndex}"]`);
                if (sectionElement) {
                    sectionElement.scrollIntoView({ behavior: "smooth", block: "center" });
                }
            }, 100);

            if (onContentUpdate) {
                onContentUpdate();
            }
        } catch (error) {
            console.error("Failed to add code section:", error);
            alert("Failed to add code section. Please try again.");
            setNewlyAddedIndex(null); // Reset on error
        } finally {

        }
    };

    // 🧠 Parse & render HTML safely with editable code sections
    const parseContent = useMemo(() => {
        if (!selectedContent?.content_text) return null;

        try {
            const predictOutputStore = [];
            let htmlToClean = selectedContent.content_text;
            
            // Extract attributes and replace elements with placeholders to avoid DOMPurify string sanitization bugs (supports class in any order)
            const regex = /<div\s+([^>]*?class=["']predict-output["'][^>]*?)>\s*<\/div>/gi;
            htmlToClean = htmlToClean.replace(regex, (match, attribsString) => {
                const getAttr = (name) => {
                    const attrRegex = new RegExp(`${name}=([\"'])([\\s\\S]*?)\\1`, "i");
                    const m = attribsString.match(attrRegex);
                    if (m) {
                        return m[2]
                            .replace(/&amp;/g, "&")
                            .replace(/&quot;/g, '"')
                            .replace(/&#10;/g, "\n")
                            .replace(/&lt;/g, "<")
                            .replace(/&gt;/g, ">");
                    }
                    return "";
                };
                
                const code = getAttr("data-code");
                const optionsStr = getAttr("data-options");
                const correct = getAttr("data-correct");
                const explanation = getAttr("data-explanation");
                
                const index = predictOutputStore.length;
                predictOutputStore.push({ code, optionsStr, correct, explanation });
                
                return `<div class="predict-output-placeholder" data-index="${index}"></div>`;
            });

            const safeHTML = cleanHTMLContent(htmlToClean);
            let codeSectionIndex = 0;
            let firstHeadingSkipped = false;
            let firstParagraphWrapped = false;

            const parseOptions = {
                replace: (domNode) => {
                    // Skip the first h1 heading to avoid title duplication
                    if (domNode?.name === "h1") {
                        if (!firstHeadingSkipped) {
                            firstHeadingSkipped = true;
                            return <></>;
                        }
                    }

                    // Wrap the first paragraph (lesson overview) in a beautiful callout
                    if (domNode?.name === "p" && !firstHeadingSkipped && !firstParagraphWrapped) {
                        firstParagraphWrapped = true;
                        return (
                            <Box
                                className="callout note lesson-overview"
                                sx={{
                                    p: 2.5,
                                    my: 3,
                                    borderRadius: "12px",
                                    borderLeft: "4px solid #e50914",
                                    backgroundColor: isDark ? "rgba(229,9,20,0.06)" : "rgba(229,9,20,0.02)",
                                    boxShadow: isDark 
                                        ? "0 4px 20px rgba(0,0,0,0.15)" 
                                        : "0 4px 20px rgba(0,0,0,0.03)",
                                }}
                            >
                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        fontWeight: 800,
                                        color: isDark ? "#ffa4a2" : "#e50914",
                                        mb: 1,
                                        fontSize: "0.88rem",
                                        letterSpacing: 0.3,
                                        textTransform: "uppercase",
                                    }}
                                >
                                    📖 Lesson Overview
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontSize: "0.98rem",
                                        lineHeight: 1.8,
                                        fontStyle: "italic",
                                        m: 0,
                                        color: theme.palette.text.primary,
                                    }}
                                >
                                    {domToReact(domNode.children, parseOptions)}
                                </Typography>
                            </Box>
                        );
                    }

                    // Auto-assign unique ID to h2 and h3 elements for table of contents navigation
                    if (domNode?.name === "h2" || domNode?.name === "h3") {
                        const getHeadingText = (node) => {
                            if (!node) return "";
                            if (node.data) return node.data;
                            if (node.children) return node.children.map(getHeadingText).join("");
                            return "";
                        };
                        const headingText = getHeadingText(domNode).trim();
                        const id = headingText.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
                        
                        const attribs = { ...domNode.attribs };
                        if (attribs.style && typeof attribs.style === "string") {
                            const styleObj = {};
                            attribs.style.split(";").forEach(rule => {
                                const parts = rule.split(":");
                                if (parts.length >= 2) {
                                    const key = parts[0].trim();
                                    const value = parts.slice(1).join(":").trim();
                                    if (key && value) {
                                        const camelKey = key.replace(/-./g, c => c[1].toUpperCase());
                                        styleObj[camelKey] = value;
                                    }
                                }
                            });
                            attribs.style = styleObj;
                        }

                        const Component = domNode.name;
                        return (
                            <Component id={id} {...attribs}>
                                {domToReact(domNode.children, parseOptions)}
                            </Component>
                        );
                    }

                    // Intercept collapsible details blocks
                    if (domNode?.name === "details") {
                        const summaryNode = domNode.children?.find(c => c.name === "summary");
                        
                        // Extract text of summary recursively to handle formatting (e.g. <strong>)
                        const getSummaryText = (node) => {
                            if (!node) return "";
                            if (node.data) return node.data;
                            if (node.children) return node.children.map(getSummaryText).join("");
                            return "";
                        };
                        const summaryText = summaryNode ? getSummaryText(summaryNode).trim() : "Show Answer";
                        
                        const contentNodes = domNode.children?.filter(c => c !== summaryNode) || [];
                        return (
                            <CollapsibleDetails key={`details-${codeSectionIndex++}`} summaryText={summaryText}>
                                {domToReact(contentNodes, parseOptions)}
                            </CollapsibleDetails>
                        );
                    }

                    // Intercept "Predict the Output" placeholder
                    const placeholderClassName = domNode?.attribs?.class || domNode?.attribs?.className;
                    if (domNode?.name === "div" && placeholderClassName === "predict-output-placeholder") {
                        const index = parseInt(domNode.attribs["data-index"] || domNode.attribs["dataindex"]);
                        const stored = predictOutputStore[index];
                        if (stored) {
                            let parsedOptions = [];
                            try {
                                parsedOptions = JSON.parse(stored.optionsStr);
                            } catch (e) {
                                parsedOptions = stored.optionsStr
                                    .replace(/^\[|\]$/g, "")
                                    .split(",")
                                    .map(o => o.trim().replace(/^["']|["']$/g, ""));
                            }
                            return (
                                <PredictOutput
                                    code={stored.code}
                                    options={parsedOptions}
                                    correctAnswer={stored.correct}
                                    explanation={stored.explanation}
                                />
                            );
                        }
                    }

                    if (domNode?.name === "pre") {
                        const codeChild = domNode.children?.find(c => c.name === "code");
                        const preClass = domNode.attribs?.class || domNode.attribs?.className || "";
                        const styleAttr = domNode.attribs?.style || "";

                        // Extract text content recursively to check for diagram ASCII symbols
                        const getRawText = (node) => {
                            if (!node) return "";
                            if (node.data) return node.data;
                            if (node.children) return node.children.map(getRawText).join("");
                            return "";
                        };
                        const rawText = getRawText(domNode);

                        const diagramSymbols = ["┌", "├", "└", "──►", "│  ", "├──", "└──", "┌──", "═", "▼", "▲"];
                        const hasDiagramSymbol = diagramSymbols.some(sym => rawText.includes(sym));
                        const hasDiagramBorder = styleAttr.includes("border-left:5px solid #e65100") || styleAttr.includes("border-left: 5px solid #e65100");
                        const hasOutputBorder = styleAttr.includes("border-left:4px solid #1565c0") || styleAttr.includes("border-left: 4px solid #1565c0");

                        const isDiagramOrOutput = (hasDiagramSymbol || hasDiagramBorder || hasOutputBorder) && !codeChild && !preClass.includes("language-");
                        const isCodeBlock = !isDiagramOrOutput;

                        if (isCodeBlock) {
                            const section = codeSections[codeSectionIndex];
                            const currentIndex = codeSectionIndex;
                            codeSectionIndex++;

                            if (section) {
                                // Use EditableCodeSection for admin, DynamicCodeEditor for regular users
                                if (isAdmin) {
                                    return (
                                        <EditableCodeSection
                                            key={`code-section-${currentIndex}`}
                                            code={section.code}
                                            language={section.language}
                                            index={currentIndex}
                                            isAdmin={isAdmin}
                                            onSave={handleCodeSectionSave}
                                            onDelete={handleCodeSectionDelete}
                                            onAdd={handleAddCodeSectionAfter} // Add new section after this one
                                            autoEdit={newlyAddedIndex === currentIndex} // Auto-open edit mode for newly added
                                            onEditComplete={() => {
                                                // Clear the newly added flag after edit is complete
                                                if (newlyAddedIndex === currentIndex) {
                                                    setNewlyAddedIndex(null);
                                                }
                                            }}
                                        />
                                    );
                                } else {
                                    // Non-admin: use DynamicCodeEditor
                                    if (["javascript", "html", "css", "agenticai", "agentic-ai", "dsa", "python", "fastapi", "sql", "genai"].includes(section.language)) {
                                        return (
                                            <DynamicCodeEditor
                                                key={`code-section-${currentIndex}`}
                                                language={section.language}
                                                initialCode={section.code}
                                            />
                                        );
                                    }
                                }
                            }
                        }

                        // Static Visual Block Diagram or Output Box (<pre> matching diagram rules)
                        const attribs = { ...domNode.attribs };
                        let styleObj = {};

                        if (attribs.style && typeof attribs.style === "string") {
                            attribs.style.split(";").forEach(rule => {
                                const parts = rule.split(":");
                                if (parts.length >= 2) {
                                    const key = parts[0].trim();
                                    const value = parts.slice(1).join(":").trim();
                                    if (key && value) {
                                        const camelKey = key.replace(/-./g, c => c[1].toUpperCase());
                                        styleObj[camelKey] = value;
                                    }
                                }
                            });
                        }

                        return (
                            <Box
                                component="pre"
                                sx={{
                                    backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "#f9f9f9",
                                    color: theme.palette.text.primary,
                                    borderRadius: "10px",
                                    padding: "1.2rem 1.5rem",
                                    overflowX: "auto",
                                    fontSize: "0.92rem",
                                    fontFamily: "'Fira Code', 'Courier New', monospace",
                                    lineHeight: 1.6,
                                    my: 2.5,
                                    border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
                                    borderLeft: styleObj.borderLeft || `5px solid ${isDark ? "#ffa4a2" : "#e50914"}`,
                                    ...styleObj,
                                }}
                            >
                                {domToReact(domNode.children, parseOptions)}
                            </Box>
                        );
                    }
                }
            };
            return parse(safeHTML, parseOptions);
        } catch (err) {
            console.error("Parse error:", err);
            return (
                <Typography color="error" sx={{ mt: 2 }}>
                    Failed to render content.
                </Typography>
            );
        }
    }, [selectedContent?.content_text, codeSections, isAdmin, isDark, theme, handleAddCodeSectionAfter, handleCodeSectionDelete, handleCodeSectionSave, newlyAddedIndex]);

    // ✅ Show error state when fetching fails
    if (error) {
        return (
            <Paper
                elevation={0}
                sx={{
                    p: 4,
                    textAlign: "center",
                    backgroundColor: isDark ? "rgba(229,9,20,0.05)" : "rgba(229,9,20,0.02)",
                    border: `1px solid ${isDark ? "rgba(229,9,20,0.2)" : "rgba(229,9,20,0.1)"}`,
                    borderRadius: 3,
                    my: 4,
                }}
            >
                <Typography variant="h6" color="error" gutterBottom fontWeight="bold">
                    Unable to Load Lesson
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    An error occurred while fetching the content. Please check your internet connection and try again.
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<ReplayIcon />}
                    onClick={onRetry}
                    sx={{
                        background: "linear-gradient(135deg, #e50914 0%, #6a0dad 100%)",
                        color: "#ffffff",
                        fontWeight: "bold",
                        px: 3,
                        py: 1,
                        "&:hover": {
                            opacity: 0.9,
                        }
                    }}
                >
                    Retry
                </Button>
            </Paper>
        );
    }

    // ✅ Show skeleton loading state when fetching
    if (loading) {
        return (
            <Box sx={{ width: "100%", py: 2 }}>
                {/* Breadcrumbs Skeleton */}
                <Skeleton variant="text" width="40%" height={24} sx={{ mb: 2 }} />
                
                {/* Title Skeleton */}
                <Skeleton variant="text" width="70%" height={48} sx={{ mb: 3 }} />
                
                {/* Metadata Row Skeleton */}
                <Box sx={{ display: "flex", gap: 1, mb: 4 }}>
                    <Skeleton variant="rectangular" width={80} height={28} sx={{ borderRadius: 1.5 }} />
                    <Skeleton variant="rectangular" width={110} height={28} sx={{ borderRadius: 1.5 }} />
                    <Skeleton variant="rectangular" width={90} height={28} sx={{ borderRadius: 1.5 }} />
                </Box>
                
                {/* Paragraphs Skeleton */}
                <Stack spacing={2} sx={{ width: "100%" }}>
                    <Skeleton variant="rectangular" width="100%" height={150} sx={{ borderRadius: 2 }} />
                    <Skeleton variant="text" width="95%" height={20} />
                    <Skeleton variant="text" width="90%" height={20} />
                    <Skeleton variant="text" width="85%" height={20} />
                    <Skeleton variant="rectangular" width="100%" height={200} sx={{ borderRadius: 2 }} />
                    <Skeleton variant="text" width="98%" height={20} />
                    <Skeleton variant="text" width="92%" height={20} />
                </Stack>
            </Box>
        );
    }

    // ✅ Show fallback when nothing is selected
    if (!selectedContent) {
        return (
            <Box sx={{ p: 4, textAlign: "center" }}>
                <Typography variant="body1" color="text.secondary">
                    Select a topic from the Course Navigation to view its content.
                </Typography>
            </Box>
        );
    }

    if (selectedContent.isQuiz) {
        const moduleId = selectedContent.content_id.split(".")[0];
        return (
            <Box sx={{ p: 1 }}>
                <QuizView subject={selectedContent.subject} moduleId={moduleId} />
            </Box>
        );
    }

    const videoId = extractYouTubeId(selectedContent?.video_link);

    return (
        <Paper
            elevation={0}
            sx={{
                borderRadius: 2,
                backgroundColor: "transparent",
                color: theme.palette.text.primary,
                transition: "background-color 0.3s ease, color 0.3s ease",
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    position: "sticky",
                    top: "-24px", // offset parent padding (p: 3)
                    zIndex: 10,
                    backgroundColor: isDark
                        ? "rgba(10, 10, 10, 0.85)"
                        : "rgba(255, 255, 255, 0.85)",
                    backdropFilter: "blur(12px)",
                    borderBottom: scrolledDown ? `1px solid ${theme.palette.divider}` : "none",
                    borderRadius: 1,
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    // Hide completely when scrolled to top to prevent overlap/layout issues
                    opacity: scrolledDown ? 1 : 0,
                    visibility: scrolledDown ? "visible" : "hidden",
                    transform: scrolledDown ? "translateY(0)" : "translateY(-10px)",
                    height: scrolledDown ? "auto" : 0,
                    py: scrolledDown ? 1.5 : 0,
                    px: scrolledDown ? 2 : 0,
                    mb: scrolledDown ? 4 : 0,
                    mx: -2,
                    overflow: "hidden",
                }}
            >
                <Typography
                    variant="subtitle1"
                    sx={{
                        fontSize: "1.1rem",
                        flex: 1,
                        fontWeight: 800,
                        color: theme.palette.primary.main,
                        textAlign: "left",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        visibility: scrolledDown ? "visible" : "hidden",
                        opacity: scrolledDown ? 1 : 0,
                        transition: "all 0.2s ease-in-out",
                    }}
                >
                    {selectedContent.title.replace(/^\u2705\s*/, "")}
                </Typography>

                {/* Toggle Video Button */}
                {videoId && (
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setShowVideo((prev) => !prev)}
                        sx={{
                            ml: 2,
                            borderRadius: "8px",
                            borderColor: theme.palette.primary.main,
                            color: theme.palette.primary.main,
                            textTransform: "none",
                            fontWeight: 700,
                            "&:hover": {
                                backgroundColor: `${theme.palette.primary.main}10`,
                                borderColor: theme.palette.primary.dark,
                            },
                        }}
                    >
                        {showVideo ? "Hide Video" : "Show Video"}
                    </Button>
                )}

                {/* Focus Mode Toggle Button */}
                {onToggleFocusMode && (
                    <Tooltip title={focusMode ? "Exit Focus Mode" : "Enter Focus Mode"} placement="top">
                        <IconButton
                            size="small"
                            onClick={onToggleFocusMode}
                            sx={{
                                ml: 1.5,
                                color: focusMode ? theme.palette.primary.main : theme.palette.text.secondary,
                                "&:hover": { color: theme.palette.primary.main },
                                transition: "all 0.2s ease",
                            }}
                        >
                            {focusMode ? <FullscreenExitIcon /> : <FullscreenIcon />}
                        </IconButton>
                    </Tooltip>
                )}
            </Box>

            {/* Video Section */}
            {showVideo && videoId && (
                <Box
                    sx={{
                        position: "relative",
                        width: "100%",
                        paddingTop: "56.25%", // 16:9 Aspect Ratio
                        mb: 4,
                        borderRadius: 3,
                        overflow: "hidden",
                        backgroundColor: theme.palette.background.default,
                        boxShadow: isDark
                            ? "0 10px 30px rgba(0,0,0,0.5), inset 0 0 1px 1px rgba(255,255,255,0.1)"
                            : "0 10px 30px rgba(0,0,0,0.1), inset 0 0 1px 1px rgba(0,0,0,0.05)",
                    }}
                >
                    {selectedContent.video_locked ? (
                        /* Premium Lock Placeholder */
                        <Box
                            sx={{
                                position: "absolute",
                                inset: 0,
                                background: isDark
                                    ? "linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 100%)"
                                    : "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                px: 4,
                                textAlign: "center"
                            }}
                        >
                            <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <Box sx={{
                                    width: 80, height: 80, borderRadius: '50%',
                                    background: `linear-gradient(135deg, ${colors.primary}20, ${colors.secondary}20)`,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    mb: 2, border: `1px solid ${colors.primary}40`
                                }}>
                                    <LockPersonIcon sx={{ fontSize: 40, color: colors.primary }} />
                                </Box>

                                <Typography variant="h5" fontWeight={900} gutterBottom sx={{
                                    background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                                    WebkitBackgroundClip: "text",
                                    WebkitTextFillColor: "transparent",
                                }}>
                                    Video Content Restricted
                                </Typography>

                                <Typography variant="body1" sx={{ color: theme.palette.text.secondary, mb: 3, maxWidth: 450 }}>
                                    {!user
                                        ? "Sign in to unlock exclusive video tutorials and master this concept with expert guidance."
                                        : "This video is part of our Premium curriculum. Please enroll in the course to gain lifetime access."}
                                </Typography>

                                <Stack direction="row" spacing={2} sx={{ flexWrap: "wrap", justifyContent: "center", gap: 2 }}>
                                    {!user ? (
                                        <>
                                            <Button
                                                variant="contained"
                                                component={RouterLink}
                                                to="/signin"
                                                state={{ from: location }}
                                                startIcon={<AccountCircleIcon />}
                                                sx={{
                                                    borderRadius: "12px", px: 4, py: 1.2, fontWeight: 700,
                                                    background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                                                    boxShadow: `0 8px 20px ${colors.primary}40`
                                                }}
                                            >
                                                Sign In
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                component={RouterLink}
                                                to="/signup"
                                                sx={{ borderRadius: "12px", px: 4, py: 1.2, fontWeight: 700, borderColor: colors.primary, color: colors.primary }}
                                            >
                                                Join Now
                                            </Button>
                                        </>
                                    ) : (
                                        <Button
                                            variant="contained"
                                            component={RouterLink}
                                            to={`/curriculum/${selectedContent.subject}`}
                                            startIcon={<SubscriptionsIcon />}
                                            sx={{
                                                borderRadius: "12px", px: 4, py: 1.2, fontWeight: 700,
                                                background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                                                boxShadow: `0 8px 20px ${colors.primary}40`
                                            }}
                                        >
                                            Enroll to Unlock
                                        </Button>
                                    )}
                                </Stack>
                            </Box>
                        </Box>
                    ) : (
                        <iframe
                            src={`https://www.youtube.com/embed/${videoId}`}
                            title="YouTube video"
                            style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: "100%",
                                border: "none",
                                borderRadius: "8px",
                            }}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        />
                    )}
                </Box>
            )}

            {/* No Video */}
            {/* {!videoId && (
                <Box
                    sx={{
                        mb: 2,
                        py: 1.5,
                        textAlign: "center",
                        border: `1px dashed ${theme.palette.divider}`,
                        borderRadius: 2,
                        color: theme.palette.text.secondary,
                        backgroundColor: theme.palette.action.hover,
                        fontStyle: "italic",
                        minHeight: 80,
                    }}
                >
                    🚫 No Video Available
                </Box>
            )} */}

            {/* Admin Add Code Section Button */}
            {isAdmin && (
                <Box sx={{ p: 2, display: "flex", justifyContent: "flex-end" }}>
                    <Tooltip title="Add New Code Section">
                        <IconButton
                            color="primary"
                            onClick={handleAddCodeSection}
                            sx={{
                                border: `2px dashed ${theme.palette.primary.main}`,
                                borderRadius: 2,
                                p: 1,
                            }}
                        >
                            <AddIcon />
                            <Typography variant="body2" sx={{ ml: 1 }}>
                                Add Code Section
                            </Typography>
                        </IconButton>
                    </Tooltip>
                </Box>
            )}

            {/* Parsed HTML Content */}
            <Box
                className="content-body"
                sx={{
                    backgroundColor: isDark
                        ? "rgba(20, 20, 25, 0.65)"
                        : "rgba(255, 255, 255, 0.75)",
                    backdropFilter: "blur(12px)",
                    border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
                    color: theme.palette.text.primary,
                    p: { xs: 1.5, sm: 2.5, md: 3 },
                    borderRadius: 3,
                    fontSize: "0.95rem",
                    lineHeight: 1.75,
                    // SVG diagrams (isolated to HTML content to prevent styling collisions with LessonHeader icons)
                    "& .parsed-html svg": {
                        display: "block",
                        maxWidth: "100%",
                        height: "auto",
                        margin: "2rem auto",
                        visibility: "visible !important",
                        opacity: "1 !important",
                        "& text": {
                            visibility: "visible !important",
                            opacity: "1 !important",
                        }
                    },
                    // Paragraph spacing (keep — layout only)
                    "& p": {
                        mb: 1.5,
                    },
                }}
            >
                <LessonHeader
                    subject={selectedContent.subject}
                    contentId={selectedContent.content_id}
                    title={selectedContent.title}
                    contentText={selectedContent.content_text}
                    focusMode={focusMode}
                    onToggleFocusMode={onToggleFocusMode}
                />
                <Box className="parsed-html" sx={{ width: "100%" }}>
                    {parseContent}
                </Box>
            </Box>
        </Paper>
    );
}
